import { InjectionToken } from '@angular/core';

import { BRAND_CONFIGS, Brand, getBrand, isCatenoHost } from '@shared/services';

/**
 * Branding for the maintenance page (KOR-15174).
 *
 * Unlike {@link getBrand}, which defaults every non-SALT host to Cateno, the maintenance page is
 * only Cateno-branded on an actual cateno.ai host; SALT hosts get SALT branding, and every other
 * host keeps the original RocketFNCL "LinX" page.
 */
export interface MaintenanceBranding {
  logo: string;
  logoAlt: string;
  /** Flip a light-on-transparent logo to dark for the white card (see `BrandConfig.defaultLogoInvert`). */
  logoInvert: boolean;
  /** A wide text wordmark (Cateno, SALT), sized shorter than the square rocket mark. */
  wordmarkLogo: boolean;
  /** Name used in "<productName> is under scheduled maintenance". */
  productName: string;
  contactHref: string;
  contactLabel: string;
  /** Open the contact link in a new tab — off for `mailto:`, which would otherwise leave a blank tab. */
  contactInNewTab: boolean;
}

const CATENO_SUPPORT_EMAIL = 'support@cateno.ai';
// Kept separate from `BRAND_CONFIGS[Brand.Cateno].contactUrl` on purpose: that value is Cateno's
// and may move to a Cateno page, while this fallback must stay RocketFNCL (KOR-15174).
const ROCKET_CONTACT_URL = 'https://rocketfncl.com/contact/';

const CATENO_BRANDING: MaintenanceBranding = {
  logo: BRAND_CONFIGS[Brand.Cateno].logo,
  logoAlt: BRAND_CONFIGS[Brand.Cateno].logoAlt,
  logoInvert: BRAND_CONFIGS[Brand.Cateno].defaultLogoInvert,
  wordmarkLogo: true,
  productName: 'Cateno',
  contactHref: `mailto:${CATENO_SUPPORT_EMAIL}`,
  contactLabel: CATENO_SUPPORT_EMAIL,
  contactInNewTab: false,
};

const SALT_BRANDING: MaintenanceBranding = {
  logo: BRAND_CONFIGS[Brand.Salt].logo,
  logoAlt: BRAND_CONFIGS[Brand.Salt].logoAlt,
  logoInvert: BRAND_CONFIGS[Brand.Salt].defaultLogoInvert,
  wordmarkLogo: true,
  productName: BRAND_CONFIGS[Brand.Salt].displayName,
  contactHref: BRAND_CONFIGS[Brand.Salt].contactUrl,
  contactLabel: BRAND_CONFIGS[Brand.Salt].contactUrl,
  contactInNewTab: true,
};

const ROCKET_BRANDING: MaintenanceBranding = {
  logo: './assets/images/rocket.svg',
  logoAlt: 'LinX',
  logoInvert: false,
  wordmarkLogo: false,
  productName: 'LinX',
  contactHref: ROCKET_CONTACT_URL,
  contactLabel: ROCKET_CONTACT_URL,
  contactInNewTab: true,
};

export const getMaintenanceBranding = (host: string = window.location.host): MaintenanceBranding => {
  if (isCatenoHost(host)) {
    return CATENO_BRANDING;
  }

  return getBrand(host) === Brand.Salt ? SALT_BRANDING : ROCKET_BRANDING;
};

export const MAINTENANCE_BRANDING = new InjectionToken<MaintenanceBranding>('Maintenance Branding', {
  providedIn: 'root',
  factory: () => getMaintenanceBranding(),
});
